import { useState, useEffect } from 'react';
import { useTranslationWithSelfFallback } from '@i18n/useTranslationWithSelfFallback';
import cn from 'classnames';
import {
  CaretRight,
  CaretDown,
  BuildingOffice,
  ChatCircleDots,
  Question,
  GlobeSimple,
} from '@phosphor-icons/react';
import { useStoresUI } from '@context/StoresContext';
import { useUI } from '@context/GlobalUIContext';
import { useHasMounted } from '@hooks/useHasMounted/useHasMounted';
import { buildHoursMessaging } from '@utils/store/buildHoursMessaging';
import { weeklyHours } from '@utils/store';
import { useIceChat } from '@hooks/useIceChat';
import {
  MegaMenuLevels,
  trackMegaMenuNavigation,
} from '_utils/GA4/trackMegaMenuEvents';
import IconWrapper from '../../../components/ui/Icons';
import type { MenuItem } from '../types';
import { useMegaMenuMobile } from '@hooks/useMegaMenuMobile';
import styles from './Mobile.module.scss';

interface RootPanelProps {
  items: MenuItem[];
  locale: string;
  alternateLocaleUrl?: string;
}

export function RootPanel({
  items,
  locale,
  alternateLocaleUrl,
}: RootPanelProps) {
  const { handleItemClick, handleClose } = useMegaMenuMobile();
  const t = useTranslationWithSelfFallback('common');
  const { favouriteStoreDetails } = useStoresUI();
  const { openStoresModal } = useUI();
  const openChat = useIceChat();
  const hasMounted = useHasMounted();

  const [storeHoursMessage, setStoreHoursMessage] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (!favouriteStoreDetails?.data) {
      setStoreHoursMessage(null);
      return;
    }
    try {
      const orderedWeek = weeklyHours(
        favouriteStoreDetails.data.store_hours,
        favouriteStoreDetails.data.store_hour_exceptions
      );
      if (orderedWeek) {
        const todaysHours = buildHoursMessaging(new Date(), orderedWeek, t);
        setStoreHoursMessage(todaysHours?.navigationMessage ?? null);
      } else {
        setStoreHoursMessage(null);
      }
    } catch {
      setStoreHoursMessage(null);
    }
  }, [favouriteStoreDetails?.data, t]);

  return (
    <div
      className={styles.Panel}
      role="group"
      aria-label={t('header.navigation.aria.label')}
    >
      <div className={styles.LinksSection}>
        <ul className={styles.List}>
          {items.map((item) => {
            const trackItem = () =>
              trackMegaMenuNavigation({
                level: MegaMenuLevels.level_1,
                linkUrl: item.url ?? null,
                linkText: item.label,
                menuPath: item.label,
              });

            return (
              <li key={item.label} className={styles.ListItem}>
                {item.linkColumns ? (
                  <button
                    className={cn(styles.Row, {
                      [styles.RowHighlight]: item.highlight,
                    })}
                    onClick={() => {
                      trackItem();
                      handleItemClick(item);
                    }}
                  >
                    {item.label}
                    <CaretRight size={20} />
                  </button>
                ) : (
                  <a
                    href={item.url ?? '#'}
                    className={cn(styles.Row, {
                      [styles.RowHighlight]: item.highlight,
                    })}
                    onClick={() => {
                      trackItem();
                      handleClose();
                    }}
                  >
                    {item.label}
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      <div className={styles.UtilitySection}>
        <button
          className={styles.UtilityStoreButton}
          onClick={() => {
            const storeLinkText =
              hasMounted && favouriteStoreDetails
                ? `${favouriteStoreDetails.data.store_name} ${t(
                    'header.storeInformation.store'
                  )}`
                : t('header.storeInformation.setMyStore');
            trackMegaMenuNavigation({
              level: MegaMenuLevels.level_1,
              linkUrl: null,
              linkText: storeLinkText,
              menuPath: storeLinkText,
            });
            openStoresModal();
          }}
          aria-label={
            hasMounted && favouriteStoreDetails
              ? `${favouriteStoreDetails.data.store_name} ${t(
                  'header.storeInformation.store'
                )}`
              : t('header.storeInformation.setMyStore')
          }
        >
          <BuildingOffice size={20} className={styles.UtilityIcon} />
          <span className={styles.UtilityStoreText}>
            {hasMounted && favouriteStoreDetails ? (
              <>
                <span className={styles.UtilityStoreName}>
                  {favouriteStoreDetails.data.store_name}{' '}
                  {t('header.storeInformation.store')}
                </span>
                {storeHoursMessage && (
                  <span className={styles.UtilityStoreHours}>
                    {storeHoursMessage}
                  </span>
                )}
              </>
            ) : (
              <span className={styles.UtilityStoreName}>
                {t('header.storeInformation.setMyStore')}
              </span>
            )}
          </span>
          <CaretDown size={16} className={styles.UtilityStoreChevron} />
        </button>
        <a
          href={`/${locale}/explore/learn`}
          className={styles.UtilityLink}
          onClick={() => {
            trackMegaMenuNavigation({
              level: MegaMenuLevels.level_1,
              linkUrl: `/${locale}/explore/learn`,
              linkText: t('footer.redesign.expertAdvice'),
              menuPath: t('footer.redesign.expertAdvice'),
            });
            handleClose();
          }}
        >
          <IconWrapper
            icon="mecLogoOutline"
            fill="none"
            width={15}
            height={20}
            classNames={styles.UtilityIcon}
          />
          {t('footer.redesign.expertAdvice')}
        </a>
        <button
          className={styles.UtilityLink}
          onClick={() => {
            trackMegaMenuNavigation({
              level: MegaMenuLevels.level_1,
              linkUrl: null,
              linkText: t('footer.redesign.chatWithUs'),
              menuPath: t('footer.redesign.chatWithUs'),
            });
            handleClose();
            openChat();
          }}
        >
          <ChatCircleDots size={20} className={styles.UtilityIcon} />
          {t('footer.redesign.chatWithUs')}
        </button>
        <a
          href={`/${locale}/explore/support`}
          className={styles.UtilityLink}
          onClick={() => {
            trackMegaMenuNavigation({
              level: MegaMenuLevels.level_1,
              linkUrl: `/${locale}/explore/support`,
              linkText: t('footer.redesign.helpCentre'),
              menuPath: t('footer.redesign.helpCentre'),
            });
            handleClose();
          }}
        >
          <Question size={20} className={styles.UtilityIcon} />
          {t('footer.redesign.helpCentre')}
        </a>
        <a
          href={alternateLocaleUrl ?? `/${locale === 'en' ? 'fr' : 'en'}`}
          className={styles.UtilityLink}
          onClick={() => {
            trackMegaMenuNavigation({
              level: MegaMenuLevels.level_1,
              linkUrl:
                alternateLocaleUrl ?? `/${locale === 'en' ? 'fr' : 'en'}`,
              linkText: t('alternate.locale.link'),
              menuPath: t('alternate.locale.link'),
            });
            handleClose();
          }}
        >
          <GlobeSimple size={20} className={styles.UtilityIcon} />
          {t('alternate.locale.link')}
        </a>
      </div>
    </div>
  );
}
